import React, { useMemo, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { ChevronDown, CheckCircle2 } from '../lib/icons';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface HomePickerProps {
  label?: string;
  compact?: boolean;
}

const HomePicker: React.FC<HomePickerProps> = ({ label = 'Home', compact = false }) => {
  const { homes, activeHome, setActiveHomeById } = useActiveHome();
  const [open, setOpen] = useState(false);

  const displayValue = useMemo(() => {
    if (!activeHome) return 'Select Home';
    return compact
      ? activeHome.address
      : `${activeHome.address}, ${activeHome.city}, ${activeHome.state}`;
  }, [activeHome, compact]);

  if (homes.length <= 1) {
    return null;
  }

  return (
    <>
      <View style={styles.container}>
        <Text style={styles.label}>{label}</Text>
        <Pressable style={styles.trigger} onPress={() => setOpen(true)}>
          <Text style={styles.valueText}>{displayValue}</Text>
          <ChevronDown size={18} color="#6b7280" />
        </Pressable>
      </View>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <View style={styles.overlay}>
          <Pressable style={styles.backdrop} onPress={() => setOpen(false)} />
          <View style={styles.dropdown}>
            {homes.map((home) => {
              const selected = activeHome?.id === home.id;
              return (
                <Pressable
                  key={home.id}
                  style={[styles.option, selected && styles.optionSelected]}
                  onPress={() => {
                    setActiveHomeById(home.id);
                    setOpen(false);
                  }}
                >
                  <View style={styles.optionTextWrap}>
                    <Text style={[styles.optionTitle, selected && styles.optionTitleSelected]}>
                      {home.address}
                    </Text>
                    <Text style={styles.optionSubtitle}>
                      {home.city}, {home.state} {home.zip_code || ''}
                    </Text>
                  </View>
                  {selected && <CheckCircle2 size={18} color="#2563eb" />}
                </Pressable>
              );
            })}
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  label: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  trigger: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  valueText: {
    color: '#111827',
    fontSize: 14,
    flex: 1,
  },
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  dropdown: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    maxHeight: '70%',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
    }),
  },
  option: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  optionSelected: {
    backgroundColor: '#eff6ff',
  },
  optionTextWrap: {
    flex: 1,
    gap: 2,
  },
  optionTitle: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  optionTitleSelected: {
    color: '#1d4ed8',
  },
  optionSubtitle: {
    fontSize: 12,
    color: '#6b7280',
  },
});

export default HomePicker;
